import { lazy, Suspense, useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { HeroSection } from "@/components/home/HeroSection";
import { SEO } from "@/components/seo/SEO";
import { useHomeContent } from "@/components/home/ContentLoader";
import { FeaturedChallengeSection } from "@/components/home/FeaturedChallengeSection";
import { useLanguage } from "@/contexts/LanguageContext";
import { Skeleton } from "@/components/ui/skeleton";
import { generateFAQStructuredData } from "@/utils/seo/structuredData";

// Import components directly to avoid dynamic import issues
import { FeaturedChannels } from "@/components/home/FeaturedChannels";
import { FeaturedPlaylists } from "@/components/home/FeaturedPlaylists";
import { FeaturedClips } from "@/components/home/FeaturedClips";
import { FeaturedDerivatives } from "@/components/home/FeaturedDerivatives";
import { FeaturedCreators } from "@/components/home/FeaturedCreators";

// Loading fallback component
const SectionSkeleton = () => (
  <div className="w-full py-9">
    <div className="container mx-auto">
      <Skeleton className="h-10 w-1/3 mb-4" />
      <Skeleton className="h-6 w-1/2 mb-8" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-48 rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

const Index = () => {
  const { t, language } = useLanguage();
  const { 
    loading, 
    channels, 
    playlists, 
    clips, 
    derivatives, 
    creators,
  } = useHomeContent();
  
  // Enhanced debugging for derivatives data
  useEffect(() => {
    if (derivatives && derivatives.length > 0) {
      console.log("Index page - loaded derivatives:", derivatives.length);
      console.log("Index page - ALL derivatives data:", 
        derivatives.map(d => ({
          id: d.id,
          title: d.title?.substring(0, 20),
          views: typeof d.views === 'number' ? d.views : `Invalid type: ${typeof d.views}`,
          likes: typeof d.likesCount === 'number' ? d.likesCount : `Invalid type: ${typeof d.likesCount}`,
          favorites: typeof d.favoritesCount === 'number' ? d.favoritesCount : `Invalid type: ${typeof d.favoritesCount}`,
          comments: typeof d.commentsCount === 'number' ? d.commentsCount : `Invalid type: ${typeof d.commentsCount}`
        }))
      );
    } else {
      console.log("Index page - no derivatives loaded or empty array");
    }
  }, [derivatives]);
  
  // Use the same movie poster image as on the /LamourCestSurcote page
  const moviePosterUrl = "/lovable-uploads/0259c719-1daa-46b5-bd60-cf6e7ee472b9.png";
  
  // Translations for the featured challenge
  const challengeTitle = "L'amour c'est surcoté";
  const challengeDescription = language === 'en' 
    ? "Boost your TikTok visibility with StudioCanal and ReCut! Create viral shorts from exclusive clips of the event film \"L'amour c'est surcoté\" with Hakim Jemili and Laura Felpin. Gain popularity, increase your views and finally monetize your remixing talent!" 
    : "Fais exploser ta visibilité sur TikTok avec StudioCanal et ReCut ! Crée des shorts viraux à partir d'extraits exclusifs du film événement « L'amour c'est surcoté » avec Hakim Jemili et Laura Felpin. Gagne en notoriété, fais monter ton nombre de vues et monétise enfin ton talent de remixeur !";
  
  const buttonText = language === 'en' ? "Discover the challenge" : "Découvrir le challenge";
  
  // Prepare SEO structured data - FAQ example for homepage
  const faqStructuredData = generateFAQStructuredData([
    {
      question: language === 'en' ? "What is ReCut?" : "Qu'est-ce que ReCut ?",
      answer: language === 'en' 
        ? "ReCut is a platform that helps video creators discover, download, and remix protected content while ensuring proper licensing and monetization opportunities."
        : "ReCut est une plateforme qui aide les créateurs vidéo à découvrir, télécharger et remixer du contenu protégé tout en garantissant des licences appropriées et des opportunités de monétisation."
    },
    {
      question: language === 'en' ? "How can I participate in a challenge?" : "Comment puis-je participer à un challenge ?",
      answer: language === 'en'
        ? "Browse our challenges section, choose a challenge that interests you, download the licensed clips, create your remix, and submit it through the platform."
        : "Parcourez notre section challenges, choisissez un challenge qui vous intéresse, téléchargez les clips sous licence, créez votre remix et soumettez-le via la plateforme."
    },
    {
      question: language === 'en' ? "Can I monetize my remix videos?" : "Puis-je monétiser mes vidéos remix ?",
      answer: language === 'en'
        ? "Yes, ReCut offers various monetization opportunities for creators who remix licensed content through our platform, including view-based rewards and challenge prizes."
        : "Oui, ReCut offre diverses opportunités de monétisation aux créateurs qui remixent du contenu sous licence via notre plateforme, notamment des récompenses basées sur les vues et des prix de challenge."
    }
  ]);
  
  // Enhanced meta description based on language
  const metaDescription = language === 'en'
    ? "ReCut - The video creation platform that transforms your content into opportunities. Discover, download, and remix protected content with proper licensing."
    : "ReCut - La plateforme de création vidéo qui transforme votre contenu en opportunités. Découvrez, téléchargez et remixez du contenu protégé avec des licences appropriées.";
  
  // Enhanced keywords based on language
  const keywords = language === 'en'
    ? "recut, video creation, monetization, creators, content, video editing, video challenges, remix, derivative works, licensed content"
    : "recut, création vidéo, monétisation, créateurs, contenu, montage vidéo, challenges vidéo, remix, œuvres dérivées, contenu sous licence";
  
  // Change the launch date to 8:00 GMT
  const launchDate = new Date('2025-04-25T08:00:00+02:00');
  
  return (
    <Layout>
      <SEO 
        title={t('home.hero.title')}
        description={metaDescription}
        keywords={keywords}
        ogUrl="/"
        canonicalUrl="/"
        structuredData={faqStructuredData}
      />
      
      <div className="w-full max-w-full overflow-x-hidden">
        <HeroSection />
        
        <FeaturedChallengeSection
          title={challengeTitle}
          description={challengeDescription}
          imageUrl={moviePosterUrl}
          linkUrl="/LamourCestSurcote"
          buttonText={buttonText}
        />
        
        {!loading && (
          <>
            <Suspense fallback={<SectionSkeleton />}>
              <FeaturedChannels channels={channels} />
            </Suspense>
            
            <Suspense fallback={<SectionSkeleton />}>
              <FeaturedPlaylists playlists={playlists} />
            </Suspense>
            
            <Suspense fallback={<SectionSkeleton />}>
              <FeaturedClips clips={clips} />
            </Suspense>
            
            {/* Check derivatives data presence and add console log */}
            {derivatives && derivatives.length > 0 && (
              <>
                {console.log("About to render FeaturedDerivatives with:", derivatives.length, "items")}
                <Suspense fallback={<SectionSkeleton />}>
                  <FeaturedDerivatives derivatives={derivatives} />
                </Suspense>
              </>
            )}
            
            {creators.length > 0 && (
              <Suspense fallback={<SectionSkeleton />}>
                <FeaturedCreators creators={creators} />
              </Suspense>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Index;
